import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { Geography } from "@/components/dm/AdminGeo";
import { Referral } from "@/components/dm/AdminReferral";
import { AdminAlerts } from "@/components/dm/AdminAlerts";
import { AdminSpotlight } from "@/components/dm/AdminSpotlight";

const useA = () => { const { lang } = useApp(); return (en, fr) => (lang === "fr" ? fr : en); };
const Stat = ({ label, value, testid }) => <div data-testid={testid} className="rounded-2xl bg-[#FFFDF9] border border-[#E8E2D9] p-4"><div className="text-xs font-semibold text-[#6B5E55]">{label}</div><div className="text-2xl font-extrabold text-[#1E4D3B]">{value}</div></div>;
const btn = "press min-h-[40px] px-3 rounded-full text-sm font-bold";

const Overview = () => {
  const a = useA(); const { L, hubOf, cat } = useApp();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/overview").then((r) => setD(r.data)); }, []);
  if (!d) return null;
  const list = (obj, name) => Object.entries(obj).map(([k, v]) => <div key={k} className="flex justify-between py-1.5 border-b border-[#E8E2D9] text-sm"><span>{name(k) || k}</span><b>{v}</b></div>);
  return (
    <div className="space-y-4">
      <AdminAlerts />
      <AdminSpotlight />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat testid="admin-users" label={a("Users", "Utilisateurs")} value={d.users} /><Stat testid="admin-verified" label={a("Verified users", "Vérifiés")} value={d.verified_users} />
        <Stat label="SMS_PROVIDER" value={d.sms_provider} testid="admin-sms-provider" />
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        <div className="rounded-2xl bg-[#FFFDF9] border p-4"><h3 className="font-bold mb-2">{a("Posts by hub", "Publications par ville")}</h3>{list(d.posts_by_hub, (k) => L(hubOf(k)))}</div>
        <div className="rounded-2xl bg-[#FFFDF9] border p-4"><h3 className="font-bold mb-2">{a("Posts by category", "Par catégorie")}</h3>{list(d.posts_by_category, (k) => L(cat(k)))}</div>
        <div className="rounded-2xl bg-[#FFFDF9] border p-4"><h3 className="font-bold mb-2">{a("Waitlist by city", "Liste d'attente")}</h3>{list(d.waitlist_by_hub, (k) => L(hubOf(k)))}</div>
      </div>
    </div>
  );
};

const Metrics = () => {
  const a = useA(); const { meta, L } = useApp();
  const [q, setQ] = useState({ hub: "", date_from: "", date_to: "" });
  const [d, setD] = useState(null);
  useEffect(() => { const p = {}; Object.entries(q).forEach(([k, v]) => v && (p[k] = v)); api.get("/admin/metrics", { params: p }).then((r) => setD(r.data)); }, [q]);
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <select data-testid="metrics-hub" value={q.hub} onChange={(e) => setQ({ ...q, hub: e.target.value })} className="min-h-[44px] rounded-full border px-3 bg-[#FFFDF9]"><option value="">{a("All hubs", "Toutes les villes")}</option>{meta.hubs.map((h) => <option key={h.id} value={h.id}>{L(h)}</option>)}</select>
        <input data-testid="metrics-from" type="date" value={q.date_from} onChange={(e) => setQ({ ...q, date_from: e.target.value })} className="min-h-[44px] rounded-full border px-3 bg-[#FFFDF9]" />
        <input data-testid="metrics-to" type="date" value={q.date_to} onChange={(e) => setQ({ ...q, date_to: e.target.value })} className="min-h-[44px] rounded-full border px-3 bg-[#FFFDF9]" />
      </div>
      {d && <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="metrics-grid">
        <Stat label={a("Contact events", "Contacts")} value={d.contact_events} /><Stat label={a("Real posts", "Publications réelles")} value={d.real_posts} />
        <Stat label={a("% real posts with ≥1 contact", "% publications avec ≥1 contact")} value={`${d.pct_posts_with_contact}%`} />
        <Stat label={a("% requests contacted within 7d", "% demandes contactées en 7 j")} value={`${d.pct_requests_contacted_7d}%`} />
        <Stat label={a("Outcomes yes / no / talking", "Résultats oui / non / en cours")} value={`${d.outcomes.yes || 0} / ${d.outcomes.no || 0} / ${d.outcomes.still_talking || 0}`} />
        <Stat label={a("% posts with contact check", "% avec vérification")} value={`${d.pct_posts_with_contact_check}%`} />
        <Stat label={a("Repeat posters (30d)", "Publieurs récurrents")} value={d.repeat_posters} /><Stat label={a("Sold / Found", "Vendu / Trouvé")} value={d.sold_or_found} />
        <Stat label={a("Vouches", "Recommandations")} value={d.vouches} /><Stat label={a("Reports", "Signalements")} value={d.reports} />
      </div>}
    </div>
  );
};

const Reports = () => {
  const a = useA(); const { t } = useApp();
  const [d, setD] = useState(null);
  const load = () => api.get("/admin/reports").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);
  const act = (url) => api.post(url).then(() => { toast.success("OK"); load(); });
  if (!d) return null;
  return (
    <div className="space-y-5">
      <h3 className="font-extrabold">{a("Hidden pending review", "Masquées en attente")} ({d.hidden_posts.length})</h3>
      {d.hidden_posts.map((p) => (
        <div key={p.id} data-testid={`hidden-post-${p.id}`} className="rounded-2xl bg-[#FFF4EA] border p-3 flex flex-wrap gap-2 items-center">
          <Link to={`/post/${p.id}`} className="font-bold flex-1">{p.title}</Link>
          <button data-testid={`restore-${p.id}`} onClick={() => act(`/admin/posts/${p.id}/restore`)} className={`${btn} bg-[#1E4D3B] text-white`}>{a("Restore", "Rétablir")}</button>
          <button data-testid={`remove-${p.id}`} onClick={() => act(`/admin/posts/${p.id}/remove`)} className={`${btn} bg-[#B91C1C] text-white`}>{a("Remove", "Retirer")}</button>
        </div>
      ))}
      <h3 className="font-extrabold">{a("All reports", "Tous les signalements")}</h3>
      {d.reports.map((r) => {
        const owner = r.post?.poster || r.profile;
        return (
          <div key={r.id} data-testid={`report-row-${r.id}`} className="rounded-2xl bg-[#FFFDF9] border p-3 space-y-2">
            <div className="text-sm"><b>{t(`r_${r.reason}`)}</b> · {r.reported_item_type} · <span className="text-[#6B5E55]">{r.status}</span> · {new Date(r.created_at).toLocaleString()}</div>
            <div className="text-sm">{r.post ? <Link className="underline" to={`/post/${r.post.id}`}>{r.post.title}</Link> : r.profile ? <Link className="underline" to={`/u/${r.profile.id}`}>{r.profile.name}</Link> : r.question ? <Link className="underline" to={`/post/${r.question.post_id}#questions`}>“{r.question.body}”{r.question.answer ? ` → ${r.question.answer}` : ""}</Link> : r.reported_item_id}{r.notes && ` — “${r.notes}”`}</div>
            <div className="flex flex-wrap gap-2">
              {r.question && r.question.status === "visible" && <button data-testid={`remove-question-${r.id}`} onClick={() => act(`/admin/questions/${r.question.id}/remove`)} className={`${btn} bg-[#F3EDE4]`}>{a("Remove question", "Retirer la question")}</button>}
              {r.post && r.post.status !== "deleted" && <button onClick={() => act(`/admin/posts/${r.post.id}/remove`)} className={`${btn} bg-[#F3EDE4]`}>{a("Remove post", "Retirer")}</button>}
              {owner && <button data-testid={`warn-${r.id}`} onClick={() => act(`/admin/users/${owner.id}/warn`)} className={`${btn} bg-[#FEF3C7]`}>{a("Warn user", "Avertir")}</button>}
              {owner && <button data-testid={`ban-${r.id}`} onClick={() => act(`/admin/users/${owner.id}/${owner.is_banned ? "unban" : "ban"}`)} className={`${btn} bg-[#291E18] text-white`}>{owner.is_banned ? a("Unban", "Débannir") : a("Ban user", "Bannir")}</button>}
              <button onClick={() => act(`/admin/reports/${r.id}/dismissed`)} className={`${btn} bg-[#F3EDE4]`}>{a("Dismiss", "Rejeter")}</button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const Businesses = () => {
  const a = useA();
  const [d, setD] = useState([]);
  const load = () => api.get("/admin/businesses").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);
  return (
    <div className="space-y-3">
      {d.map((b) => (
        <div key={b.id} data-testid={`biz-row-${b.id}`} className="rounded-2xl bg-[#FFFDF9] border p-3 flex flex-wrap items-center gap-2">
          <div className="flex-1 min-w-[200px]"><div className="font-bold">{b.business_name}</div><div className="text-xs text-[#6B5E55]">{b.owner?.name} · {b.verification_status} · tier: {b.tier}</div></div>
          <button data-testid={`biz-approve-${b.id}`} onClick={() => api.post(`/admin/businesses/${b.id}/approved`).then(load)} className={`${btn} bg-[#1E4D3B] text-white`}>{a("Approve", "Approuver")}</button>
          <button data-testid={`biz-reject-${b.id}`} onClick={() => api.post(`/admin/businesses/${b.id}/rejected`).then(load)} className={`${btn} bg-[#F3EDE4]`}>{a("Reject", "Refuser")}</button>
        </div>
      ))}
    </div>
  );
};

const Hubs = () => {
  const a = useA(); const { meta, L } = useApp();
  const [hubs, setHubs] = useState(meta.hubs);
  const [areas, setAreas] = useState(meta.areas);
  const [sel, setSel] = useState("montreal");
  const [na, setNa] = useState({ name_en: "", name_fr: "" });
  const reload = () => api.get("/meta").then((r) => { setHubs(r.data.hubs); setAreas(r.data.areas); });
  const upd = (id, body) => api.put(`/admin/hubs/${id}`, body).then(reload);
  const addArea = () => na.name_en && api.post("/admin/areas", { hub_id: sel, ...na, name_fr: na.name_fr || na.name_en, sort_order: 99 }).then(() => { setNa({ name_en: "", name_fr: "" }); reload(); });
  return (
    <div className="space-y-5">
      {hubs.map((h) => (
        <div key={h.id} data-testid={`hub-row-${h.id}`} className="rounded-2xl bg-[#FFFDF9] border p-3 flex flex-wrap items-center gap-4">
          <div className="font-bold flex-1">{L(h)}</div>
          <label className="flex items-center gap-2 text-sm font-semibold">{h.launch_status === "active" ? a("Active", "Actif") : a("Launching soon", "Bientôt")}<Switch data-testid={`hub-active-${h.id}`} checked={h.launch_status === "active"} onCheckedChange={(v) => upd(h.id, { launch_status: v ? "active" : "launching_soon" })} /></label>
          <label className="flex items-center gap-2 text-sm font-semibold">{a("Demo posts", "Publications exemples")}<Switch data-testid={`hub-demo-${h.id}`} checked={h.demo_enabled !== false} onCheckedChange={(v) => upd(h.id, { demo_enabled: v })} /></label>
        </div>
      ))}
      <div className="rounded-2xl bg-[#FFFDF9] border p-4 space-y-3">
        <select data-testid="areas-hub" value={sel} onChange={(e) => setSel(e.target.value)} className="min-h-[44px] rounded-full border px-3">{hubs.map((h) => <option key={h.id} value={h.id}>{L(h)}</option>)}</select>
        {areas.filter((x) => x.hub_id === sel).map((x) => (
          <div key={x.id} className="flex items-center gap-2 text-sm border-b py-1.5"><span className="flex-1">{x.name_en} / {x.name_fr}</span>
            <button data-testid={`area-delete-${x.id}`} onClick={() => api.delete(`/admin/areas/${x.id}`).then(reload)} className="text-[#B91C1C] font-semibold">✕</button></div>
        ))}
        <div className="flex flex-wrap gap-2">
          <input data-testid="area-new-en" value={na.name_en} onChange={(e) => setNa({ ...na, name_en: e.target.value })} placeholder="Name (EN)" className="min-h-[44px] rounded-full border px-3" />
          <input data-testid="area-new-fr" value={na.name_fr} onChange={(e) => setNa({ ...na, name_fr: e.target.value })} placeholder="Nom (FR)" className="min-h-[44px] rounded-full border px-3" />
          <button data-testid="area-add" onClick={addArea} className={`${btn} bg-[#1E4D3B] text-white`}>{a("Add area", "Ajouter")}</button>
        </div>
      </div>
    </div>
  );
};

const Users = () => {
  const a = useA();
  const [q, setQ] = useState("");
  const [d, setD] = useState([]);
  const load = () => api.get("/admin/users", { params: { q } }).then((r) => setD(r.data));
  useEffect(() => { load(); }, [q]); // eslint-disable-line react-hooks/exhaustive-deps
  const setPhone = (u) => { const p = window.prompt(a("New phone (+1...)", "Nouveau numéro (+1...)"), u.phone || "+1"); if (p) api.put(`/admin/users/${u.id}/phone`, { phone: p }).then(() => { toast.success("OK"); load(); }).catch(() => toast.error("Error")); };
  return (
    <div className="space-y-3">
      <input data-testid="admin-user-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={a("Search name or phone", "Chercher nom ou téléphone")} className="w-full min-h-[44px] rounded-full border px-4" />
      {d.map((u) => (
        <div key={u.id} data-testid={`admin-user-${u.id}`} className="rounded-2xl bg-[#FFFDF9] border p-3 flex flex-wrap items-center gap-2 text-sm">
          <div className="flex-1 min-w-[200px]"><b>{u.name}</b> {u.is_demo && "· demo"} {u.role === "admin" && "· admin"}<div className="text-xs text-[#6B5E55]">{u.phone || "—"} · {u.phone_verified ? "verified" : "unverified"} · warnings {u.warnings}{u.is_banned && " · BANNED"}</div></div>
          <button onClick={() => setPhone(u)} className={`${btn} bg-[#F3EDE4]`}>{a("Set phone", "Définir le numéro")}</button>
          <button onClick={() => api.post(`/admin/users/${u.id}/warn`).then(load)} className={`${btn} bg-[#FEF3C7]`}>{a("Warn", "Avertir")}</button>
          <button onClick={() => api.post(`/admin/users/${u.id}/${u.is_banned ? "unban" : "ban"}`).then(load)} className={`${btn} bg-[#291E18] text-white`}>{u.is_banned ? a("Unban", "Débannir") : a("Ban", "Bannir")}</button>
        </div>
      ))}
    </div>
  );
};

export default function Admin() {
  const { user, ready, t } = useApp();
  const a = useA();
  if (!ready) return null;
  if (user?.role !== "admin") return <div className="max-w-md mx-auto p-10 text-center space-y-3" data-testid="admin-forbidden"><div className="text-5xl font-extrabold text-[#C2410C]">403</div><p className="font-semibold">{t("forbidden")}</p><Link to="/" className="inline-flex min-h-[48px] px-6 items-center rounded-full bg-[#1E4D3B] text-white font-bold">{t("home")}</Link></div>;
  const tabs = [["overview", a("Overview", "Aperçu"), Overview], ["metrics", a("Metrics", "Métriques"), Metrics], ["geo", a("Geography", "Géographie"), Geography], ["referral", a("Invites", "Invitations"), Referral], ["reports", a("Reports", "Signalements"), Reports],
    ["business", a("Businesses", "Entreprises"), Businesses], ["hubs", a("Hubs & areas", "Villes et secteurs"), Hubs], ["users", a("Users", "Utilisateurs"), Users]];
  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-5" data-testid="admin-page">
      <h1 className="text-3xl font-extrabold tracking-tight">Admin</h1>
      <Tabs defaultValue="overview">
        <TabsList className="flex flex-wrap h-auto gap-1 bg-[#F3EDE4] rounded-2xl p-1">{tabs.map(([k, l]) => <TabsTrigger key={k} value={k} data-testid={`admin-tab-${k}`} className="rounded-xl min-h-[40px]">{l}</TabsTrigger>)}</TabsList>
        {tabs.map(([k, , C]) => <TabsContent key={k} value={k} className="pt-4"><C /></TabsContent>)}
      </Tabs>
    </div>
  );
}
